import React, { useEffect, useState, useMemo } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@material-ui/core/Link';

import useApi from 'app/context/useApi';
import PageLoading from 'app/components/pages/PageLoading';

const useStyles = makeStyles(theme => ({
    container: {
        minHeight: 'calc(100vh - 52px)',
        "& .link" : {
            color: theme.palette.secondary.main,
            textDecoration: 'none',
            "&:hover" : {
                textDecoration: 'underline'
            }
        }
    },
    containerAuto: {
        "& .link" : {
            color: theme.palette.secondary.main,
            textDecoration: 'none',
            "&:hover" : {
                textDecoration: 'underline'
            }
        }
    }
}));

export default function Page(props) {

    const { id, global, initPage, height="page", noSkeleton } = props;

    const api = useApi();
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const prefix = global ? '' : '/protected'

    const [ page, setPage ] = useState(null);
    const [ loadingImages, setLoadingImages ] = useState([]);
    const [ loading, setLoading ] = useState(true);

    const getPage = () => {

        api.get(`${prefix}/page/${id}`)
            .then(res => {
                setPage(res.page);
                loadImages();
            }).catch(() => setLoading(false));
    }

    useEffect(() => {
        if (! initPage) {
            getPage();
        } else {
            setPage(initPage);
            loadImages();
        }
        
    }, []);

    const loadImages = () => {
        if (page.blocks.length) {

            const imgs = [];

            page.blocks.forEach(b => {
                if (b.options.type === 'image') {
                    const img = new Image();
                    img.onload = handleImageLoaded(b.id);
                    img.src = b.file.full_url
                    imgs.push(b.id);
                }
            });

            setLoadingImages(imgs);
        }
    }

    const handleImageLoaded = (id) => {

        const imgs = [...loadingImages];

        const i = imgs.indexOf(id);

        if (i > -1) { // only splice array when item is found
            imgs.splice(index, 1); // 2nd parameter means remove one item only
        }

        if (imgs.length === 0) setLoading(false);
        setLoadingImages(imgs);
    }

    const handleLink = (block) => {

        api.get(`${prefix}/page/${page.id}/link/${block.id}`).finally(() => setLoading(false));
        
    }

    const blocks = useMemo(() => {
        if (page && page.blocks.length) {

            return page.blocks.map(b => {

                const styles = {
                    paddingTop: b.options.padding_top,
                    paddingBottom: b.options.padding_bottom,
                    paddingLeft: b.options.padding_left,
                    paddingRight: b.options.padding_right
                }

                switch (b.options.type) {
                    case 'text': 

                        styles.maxWidth = 900;
                        styles.margin = '0 auto';

                        return (
                            <div style={{ backgroundColor: b.options.background_color}}>
                                <div style={styles} dangerouslySetInnerHTML={{__html: b.options.content}} />
                            </div>
                        );

                    case 'image': 

                        const unit = b.options.image_width_type === 'pixel' ? 'px' : '%';

                        styles.textAlign = b.options.image_align;
                        styles.backgroundColor = b.options.background_color;
                    
                        return (
                            <div style={styles}>
                                <img style={{ height: 'auto', maxWidth: '100%', width: b.options.image_width + unit }} src={b.file.full_url}/>
                            </div>
                        );

                    case 'link':

                        styles.textAlign = b.options.button_align;
                        styles.maxWidth = 900;
                        styles.margin = '0 auto';

                        const linkStyles = {
                            display: 'inline-block',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            color: b.options.button_text_color, 
                            backgroundColor: b.options.button_color, 
                            borderRadius: b.options.button_radius, 
                            paddingTop: b.options.button_padding_height, 
                            paddingBottom: b.options.button_padding_height,
                            paddingLeft: b.options.button_padding_width,
                            paddingRight: b.options.button_padding_width
                        }

                        return (
                            <div style={{ backgroundColor: b.options.background_color}}>
                                <div style={styles}>
                                    {(b.options.link_type == 'link') ? (
                                        <a style={linkStyles} href={b.options.content} target={b.options.target ? '_blank' : '_self'} rel="noopener">
                                            <strong>{b.options.button_text}</strong>
                                        </a>
                                    ) : (b.options.link_type == 'page') ? (
                                        <Link component={RouterLink} to={b.options.content} style={linkStyles}>
                                            <strong>{b.options.button_text}</strong>
                                        </Link>
                                    ) : (
                                        <a style={linkStyles} onClick={() => handleLink(b)}>
                                            <strong>{b.options.button_text}</strong>
                                        </a>
                                    )}
                                </div>
                            </div>
                        )
                }
            });
        } else return null;
    }, [page]);

    return loading ? (
        <>
            {!noSkeleton &&
                <PageLoading isLoading={loading}/>
            }
        </>
    ) : (
        <div 
            className={height === 'page' ? classes.container : classes.containerAuto} 
            style={{ backgroundColor: page ? page.background_color : "#fff"}}
            >
            {blocks}
        </div>
    );
}
